import { Link } from 'react-router-dom';
import { DataTable, Card, MetricGauge, MetricCard, MetricCardGrid, METRIC_READ_CAPTIONS, SkeletonTable, Skeleton, Badge, AreaChart, DonutChart, Alert, Button, type DataTableColumn, type DonutSegment } from '@aaimplatform/ui';
import {
  useCollateralMonitoringSummary,
  useCollateralValueTrend,
  useCollateralDistribution,
  useCollateralDeficiencies,
} from '../../hooks/useCollateralMonitoring';
import type { CollateralDeficiency, AssetDistribution } from '../../api/services/collateral-monitoring';
import type { components as AdminPortalUiComponents } from '@aaimplatform/institutional-types/generated/admin-portal-ui';
import { DashboardSectionLabel, GaugeUnavailable } from './dashboard-chrome';
import { ChaTierBadge } from '../pledges/pledge-display';
import {
  SECURITY_ASSET_CLASS_LABELS,
  formatPercent,
  formatUsdCompact,
  labelFor,
  securityAssetClassColor,
} from '../monitoring/display-format';

// The page carried a search box and an Add Filter menu (asset class, LTV
// status, deficiency status) whose state no read and no row list consumed, and
// whose asset-class options (Bitcoin, Pre-IPO Equity, ...) were not classes the
// distribution read carries. They were removed rather than left as controls
// that change nothing; the monitoring endpoints accept no filter parameters to
// wire them to.

/**
 * The window the value-trend read asks for. The chart title and the empty
 * sentence both state it, so the page passes it rather than relying on the
 * route's default.
 */
const VALUE_TREND_HOURS = 8;

/** The value trend's measured-empty sentence: what is absent, and why. */
const VALUE_TREND_EMPTY_MESSAGE = `No valuations were recorded in the last ${VALUE_TREND_HOURS} hours, so there is no trend to draw.`;

/**
 * CollateralDeficiency.status is the alert's WORKFLOW state, mapped by the
 * service from CollateralAlertStatus (PENDING stays PENDING; acknowledged,
 * action taken and escalated read ACTIVE). It says nothing about severity, so
 * it renders as a neutral badge in words, never red or amber.
 */
const DEFICIENCY_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  ACTIVE: 'In progress',
  RESOLVED: 'Resolved',
};

/**
 * Shape a bare trend array for AreaChart.
 *
 * The contract declares CollateralMonitoringSummary.ltvTrend
 * (schema-registry/platform-admin.openapi.yaml:5391) and .concentrationTrend
 * (:5396) as a bare `array of number` carrying no timestamp, period, or
 * interval. The x label is therefore the point's ORDINAL POSITION in the array,
 * which is the only ordering the payload actually carries. Do not label these
 * as quarters, months, or hours: the contract supplies no such fact, and a
 * period label the data does not support is an invented reading.
 */
function toTrendSeries(points: number[]): Array<Record<string, string | number>> {
  return points.map((value, index) => ({ point: String(index + 1), value }));
}

// The withheld-read captions are the kit's METRIC_READ_CAPTIONS (GH #2917
// deliverable 5); the local names keep this page's call sites readable.
/** The read ran and rejected. */
const READ_FAILED_CAPTION = METRIC_READ_CAPTIONS.failed;
/**
 * The read has not run. Distinct from READ_FAILED_CAPTION, which states that a
 * read ran and rejected, and distinct from any caption describing a portfolio:
 * there is no portfolio here, only an absent measurement.
 */
const NOT_READ_CAPTION = METRIC_READ_CAPTIONS.unread;
/** The read succeeded and this optional field was absent from the response. */
const NOT_REPORTED_CAPTION = METRIC_READ_CAPTIONS.notReported;

/**
 * The four read outcomes, QueryReadOutcome in
 * schema-registry/admin-portal-ui.openapi.yaml.
 */
type ReadOutcome = AdminPortalUiComponents['schemas']['QueryReadOutcome'];

/**
 * The read outcome a section renders from, derived from the fields the query
 * result already carries. The hooks are plain `useQuery` and are unchanged.
 *
 * TanStack v5 defines `isLoading` as `isPending && isFetching`, so a FIRST
 * fetch that is PAUSED -- the browser offline before this page mounted,
 * networkMode default -- reports isPending with isFetching false: past the
 * skeleton branch, and neither error nor success. `unread` is that state, and
 * it is the one that reads as an empty book if nothing names it.
 *
 * `failed` is an error with NO payload: a first read that rejected. A rejected
 * background refetch over a prior successful read keeps that payload, stays
 * `read`, and is named in the page's refresh-failed notice with the time of the
 * read shown (GH #2917 deliverable 4, operator ruling 2026-09-23: the rows were
 * measured, and a failed refresh makes them older -- the staleness class of
 * GH #2383).
 *
 * This mirrors ComplianceStatus.tsx and RiskDashboard.tsx, which carry the same
 * four outcomes. Sibling dashboards must not answer the same question
 * differently.
 */
function readOutcome(query: {
  isError: boolean;
  isLoading: boolean;
  data: unknown;
}): ReadOutcome {
  if (query.isError && query.data === undefined) return 'failed';
  if (query.isLoading) return 'loading';
  if (query.data !== undefined) return 'read';
  return 'unread';
}

/**
 * An LTV percentage from the contract (avgLtv's unit, "LTV ratio as
 * percentage"), at the one decimal place the LTV gauge prints, or '--' when the
 * response carried none.
 */
function formatLtvPercent(value: number | undefined): string {
  return typeof value === 'number' ? formatPercent(value) : '--';
}

/** The local clock time of a read, for the refresh-failed notice. */
function readTime(dataUpdatedAt: number): string {
  return new Date(dataUpdatedAt).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * The body text a chart or table section renders when it has no rows to draw.
 *
 * `emptyMessage` is a MEASURED claim about an empty result set --
 * 'No active collateral deficiency notices' is a count of zero written as
 * prose, and 'No collateral distribution data available' is a statement about
 * a portfolio -- so both are reachable only from the `read` outcome. Printing
 * either with no read issued, or with the read rejected, renders an outage as
 * a healthy, empty book, which is strictly worse for an FI operator than an
 * error they can see.
 *
 * GH #2917 deliverable 2 adds the fourth case. `rows` is `undefined` when a
 * read SUCCEEDED and its body omitted the list. Every one of these lists is
 * optional in the canonical spec, so that body is contract-valid and says
 * nothing about the institution's book. The service used to answer `[]` on its
 * behalf and the measured-empty sentence printed. The absence now arrives
 * intact and gets a sentence of its own.
 */
function sectionMessage(
  outcome: ReadOutcome,
  subject: string,
  emptyMessage: string,
  rows: readonly unknown[] | undefined,
): string {
  if (outcome === 'failed') return `${subject} could not be read.`;
  if (outcome !== 'read') return `${subject} not yet read.`;
  if (rows === undefined) return `${subject} was not in the response.`;
  return emptyMessage;
}

/**
 * A list as the body carried it, or undefined when the body carried none.
 *
 * The generated type says each list is an array or absent, and the runtime body
 * is not bound by the type: a JSON `null`, or any other non-array, arrives as
 * well. Only an array is a list the response sent, so everything else is
 * treated as the absence it is.
 */
function listOrAbsent<T>(value: T[] | null | undefined): T[] | undefined {
  return Array.isArray(value) ? value : undefined;
}

/**
 * The donut segments a read actually priced.
 *
 * `AssetDistribution.value` is OPTIONAL in the canonical spec
 * (schema-registry/platform-admin.openapi.yaml), so an asset class can arrive
 * with a name and no dollar figure. `d.value ?? 0` drew that class as a 0.0
 * percent slice AND summed it as a zero in the center total: two measured
 * claims about a figure the response never carried, on the surface an FI reads
 * its collateral mix from. GH #2917 deliverable 3.
 *
 * A class with no value is withheld from the chart and named beneath it by
 * {@link unvaluedRowList}. A class whose value is a measured zero still has a
 * value, so it stays in, and `typeof === 'number'` is what keeps those two
 * apart where a truthiness test would collapse them.
 */
function valuedSegments(rows: readonly AssetDistribution[]): DonutSegment[] {
  const segments: DonutSegment[] = [];
  for (const row of rows) {
    if (typeof row.value === 'number') {
      // The color is keyed by the asset class, not by the row's position, so
      // a class draws in the color Asset Classification gives it.
      segments.push({
        label: labelFor(row.name, SECURITY_ASSET_CLASS_LABELS),
        value: row.value,
        color: securityAssetClassColor(row.name),
      });
    }
  }
  return segments;
}

/**
 * The rows the response left unvalued, as one operator-readable list, or
 * undefined when it left none. A row carrying a name is named; a row carrying
 * neither a name nor a value is counted, because naming it would mean inventing
 * the name too.
 */
function unvaluedRowList(rows: readonly AssetDistribution[]): string | undefined {
  const named: string[] = [];
  let unnamed = 0;
  for (const row of rows) {
    if (typeof row.value === 'number') continue;
    if (typeof row.name === 'string' && row.name.length > 0) {
      named.push(labelFor(row.name, SECURITY_ASSET_CLASS_LABELS));
    }
    else unnamed += 1;
  }
  if (named.length === 0 && unnamed === 0) return undefined;
  const parts = [...named];
  if (unnamed > 0) parts.push(unnamed === 1 ? '1 unnamed row' : `${unnamed} unnamed rows`);
  return parts.join(', ');
}

/**
 * CollateralMonitoringDashboard - collateral valuations, LTV and open collateral alerts
 *
 * All data fetched from backend via Prisma-backed endpoints.
 * Renders empty states when no data exists.
 */
export function CollateralMonitoringDashboard() {
  const summaryQuery = useCollateralMonitoringSummary();
  const trendQuery = useCollateralValueTrend(VALUE_TREND_HOURS);
  const distributionQuery = useCollateralDistribution();
  const deficiencyQuery = useCollateralDeficiencies();

  // GH #2913, second pass. The three sibling reads gated on their `isLoading`
  // flag alone, so an unread or rejected read fell through to the same
  // measured-empty sentence a successful empty read produces. Each now carries
  // its own outcome, and the sentences are reachable only from `read`.
  const trendOutcome = readOutcome(trendQuery);
  const distributionOutcome = readOutcome(distributionQuery);
  const deficiencyOutcome = readOutcome(deficiencyQuery);

  // Rows are the read's OUTPUT, so they exist only where a read succeeded. An
  // unread or failed sibling contributes none, which keeps a chart, a table and
  // a measured-empty sentence all unreachable on those paths. A sibling whose
  // background refetch rejected keeps the rows of its prior read, named in the
  // refresh-failed notice (GH #2917 deliverable 4).
  //
  // GH #2917 deliverable 2. `undefined` now reaches this page for two separate
  // reasons, and `sectionMessage` tells them apart: the read did not produce
  // rows, or the read succeeded and the body omitted the list. The `?? []` that
  // stood on both halves of this expression answered for the server in the
  // second case, and a measured-empty sentence then went out over a list nobody
  // received.
  //
  // Each binding passes through `listOrAbsent`, so a JSON `null` in place of a
  // list, or any other non-array, is also a list nobody received. Without it a
  // `null` distribution skipped the chart as falsy and then failed the
  // `=== undefined` test in `sectionMessage`, which printed the measured-empty
  // sentence over it.
  const trendPoints =
    trendOutcome === 'read' ? listOrAbsent(trendQuery.data?.points) : undefined;
  const distribution =
    distributionOutcome === 'read'
      ? listOrAbsent(distributionQuery.data?.distribution)
      : undefined;
  const deficiencies =
    deficiencyOutcome === 'read'
      ? listOrAbsent(deficiencyQuery.data?.deficiencies)
      : undefined;

  // The donut draws only the asset classes the read priced, and names the rest.
  // The center total is a sum over the drawn segments alone, so it states what
  // was valued and never a figure padded out with invented zeros.
  const distributionSegments = distribution ? valuedSegments(distribution) : [];
  const distributionUnvalued = distribution ? unvaluedRowList(distribution) : undefined;
  const distributionValuedTotal = distributionSegments.reduce((s, d) => s + d.value, 0);

  // GH #2913. `summary` is null until the read SUCCEEDS -- never a `?? 0`
  // stand-in. FOUR states, not two:
  //
  //   isLoading                -> skeletons
  //   isError, no prior data   -> every tile '--' + READ_FAILED_CAPTION, notice
  //   isPending && !isFetching -> every tile '--' + NOT_READ_CAPTION
  //   data in hand             -> the response's figures, real zeros included,
  //                               kept through a rejected refetch (GH #2917 d4)
  //
  // The third state is the one nothing here named. TanStack v5 defines
  // `isLoading` as `isPending && isFetching`, so a FIRST fetch that is PAUSED
  // -- the browser offline before this page mounted, networkMode default -- is
  // pending with isFetching false: past the skeleton branch, and neither error
  // nor success. The tiles fell through to `summary?.X ?? 0` and published
  // $0, 0 and '0 CRITICAL | 0 WARNING' for a portfolio nobody read. A count is
  // as much a measurement as a dollar figure.
  //
  // Every field of CollateralMonitoringSummary is optional in the contract
  // (schema-registry/platform-admin.openapi.yaml), so a field absent from a
  // SUCCESSFUL read is an absence of its own: '--' with NOT_REPORTED_CAPTION,
  // never a zero. A measured zero that IS in the response still renders as 0.
  const summary = summaryQuery.data ?? null;
  const summaryLoading = summaryQuery.isLoading;
  const summaryFailed = readOutcome(summaryQuery) === 'failed';

  // Sections whose background refetch rejected after a successful read. Their
  // prior figures stay on the page; the notice says when they were read.
  const refreshFailures = [
    { label: 'summary', query: summaryQuery },
    { label: 'value trend', query: trendQuery },
    { label: 'asset class distribution', query: distributionQuery },
    { label: 'deficiency notices', query: deficiencyQuery },
  ].filter(({ query }) => query.isError && query.data !== undefined);
  const oldestFailedRead =
    refreshFailures.length > 0
      ? Math.min(...refreshFailures.map(({ query }) => query.dataUpdatedAt))
      : null;

  // Non-null exactly when no figure may be printed, whatever the tile.
  const withheldCaption: string | null = summaryFailed
    ? READ_FAILED_CAPTION
    : summary
      ? null
      : NOT_READ_CAPTION;

  const metricValue = (
    value: number | undefined,
    format: (v: number) => string,
  ): string => (value == null ? '--' : format(value));

  // The caption is derived from the SAME value the tile prints, so the two
  // cannot tell different stories: a withheld value can never carry a caption
  // that describes a portfolio.
  //
  // GH #2917 deliverable 5 splits the one caption helper in two, because the
  // two halves were never the same kind of statement. `withheldFor` answers the
  // read and rides MetricCard's `caption` slot; `measuredNote` describes a
  // figure that printed and stays on `description`. One slot carrying both is
  // how a withheld caption came to sit where a measurement belongs.
  const withheldFor = (value: number | undefined): string | undefined =>
    withheldCaption ?? (value == null ? NOT_REPORTED_CAPTION : undefined);

  const measuredNote = (
    value: number | undefined,
    note?: string,
  ): string | undefined => (withheldFor(value) === undefined ? note : undefined);

  // The ladder is GREEN / YELLOW / ORANGE / RED. criticalAlerts counts the open
  // alerts at RED and warningAlerts every other open alert
  // (collateral-monitoring.service.ts), so the caption names them by the
  // ladder, never by the retired critical / warning severities.
  const alertBreakdown =
    summary?.criticalAlerts != null && summary?.warningAlerts != null
      ? `${summary.criticalAlerts} at Red, ${summary.warningAlerts} below Red`
      : 'Tier breakdown not reported';

  const deficiencyColumns: DataTableColumn<CollateralDeficiency>[] = [
    { header: 'Loan ID', accessor: 'loanId', align: 'left' },
    { header: 'Borrower', accessor: 'borrower', align: 'left' },
    {
      // The alert's CHA tier on the four-tier ladder (iris cw-06), the same
      // badge every pledge surface uses. The service omits a stored value off
      // the ladder, and an absent tier reads Unclassified.
      header: 'Tier',
      accessor: 'tier',
      align: 'left',
      cell: (_value, row) => <ChaTierBadge tier={row.tier} />,
    },
    {
      header: 'Current LTV',
      accessor: 'currentLTV',
      align: 'right',
      monospace: true,
      // Both fields are optional in the contract. An absent value prints '--'
      // and never enters the breach comparison; `?? 0` read it as a 0% LTV.
      cell: (_value, row) => (
        <span
          className={
            typeof row.currentLTV === 'number' &&
            typeof row.threshold === 'number' &&
            row.currentLTV > row.threshold
              ? 'text-error font-semibold'
              : ''
          }
        >
          {formatLtvPercent(row.currentLTV)}
        </span>
      ),
    },
    { header: 'Threshold', accessor: 'threshold', align: 'right', monospace: true, cell: (_value, row) => formatLtvPercent(row.threshold) },
    // The service fills this field with the alert's resolution note, or a
    // tier description when there is none. It names no collateral, so the
    // column is headed for what it holds.
    { header: 'Note', accessor: 'collateral', align: 'left' },
    {
      header: 'Status',
      accessor: 'status',
      align: 'left',
      cell: (_value, row) => (
        <Badge variant="neutral">
          {row.status == null ? '--' : labelFor(row.status, DEFICIENCY_STATUS_LABELS)}
        </Badge>
      ),
    },
  ];

  // A failed summary read used to early-return this whole page, which also
  // withheld the three sibling reads (value trend, distribution, deficiencies)
  // that had succeeded. The failure is now confined to the surfaces it owns:
  // the notice below plus withheld tiles and gauges.

  return (
    <div>
      {/* Page Heading */}
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        {/* No read on this page measures freshness, so the subtitle claims
            none; the value trend below states when valuations last arrived. */}
        <p className="text-sm font-sans text-tertiary">Collateral valuations, LTV ratios, and open collateral alerts for your institution.</p>
      </div>

      {/*
        The notice half of the withheld-read contract. Four tiles reading '--'
        with nothing said is a quieter version of the same defect: the operator
        sees withheld figures and no reason to distinguish them from a portfolio
        that is genuinely empty.
      */}
      {oldestFailedRead !== null && (
        <div className="mb-6" role="status">
          <Alert type="warning" title="Collateral monitoring refresh failed">
            <div className="space-y-2">
              <div className="text-xs">
                {`The latest refresh of ${refreshFailures.map(({ label }) => label).join(', ')} failed. The figures shown are from the read at ${readTime(oldestFailedRead)}.`}
              </div>
              <Button
                variant="tertiary"
                size="small"
                onClick={() => {
                  refreshFailures.forEach(({ query }) => {
                    void query.refetch();
                  });
                }}
              >
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {summaryFailed && (
        <div className="mb-6" role="alert">
          <Alert type="error" title="Collateral monitoring summary could not be read">
            <div className="space-y-2">
              <div className="text-xs">
                The figures below are withheld, not zero. Nothing here states
                what your institution holds -- the read itself failed.
              </div>
              <Button
                variant="tertiary"
                size="small"
                onClick={() => {
                  void summaryQuery.refetch();
                }}
              >
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {/* Key Metrics: the kit tile row, two across on a phone (UI run r3,
          fi-cons-13). One per row spent about 630px on four tiles. */}
      <MetricCardGrid className="mb-8">
        {summaryLoading ? (
          <>
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </>
        ) : (
          <>
            <MetricCard
              label="Total Collateral Value"
              value={metricValue(summary?.totalCollateralValue, formatUsdCompact)}
              caption={withheldFor(summary?.totalCollateralValue)}
            />
            {/* The same label as Portfolio's tile, which counts open alerts on
                active loans only; this one counts every open alert of the
                institution (UI run r3, fi-g8-6). */}
            <MetricCard
              label="Collateral Health Alerts"
              value={metricValue(summary?.activeAlerts, (v) => String(v))}
              caption={withheldFor(summary?.activeAlerts)}
              description={measuredNote(summary?.activeAlerts, alertBreakdown)}
            />
            {/* A measured 0.0-minute cycle is a real reading, so the test is
                `!= null` rather than truthiness, which printed '--' for it. */}
            <MetricCard
              label="M2M Cycle Performance"
              value={metricValue(summary?.m2mCycleMinutes, (v) => `${v.toFixed(1)} min`)}
              caption={withheldFor(summary?.m2mCycleMinutes)}
              description={measuredNote(summary?.m2mCycleMinutes, 'Target: 15 min')}
            />
            <MetricCard
              label="LTV Breaches (24h)"
              value={metricValue(summary?.ltvBreaches24h, (v) => String(v))}
              caption={withheldFor(summary?.ltvBreaches24h)}
            />
          </>
        )}
      </MetricCardGrid>

      {/* Gauges and Charts (F-AP-NAN-001: gate gauge render on real data,
          to avoid 0.0 / NaN% leaks when no records exist) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {summaryLoading ? (
          <>
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <div className="lg:col-span-2"><Skeleton className="h-48" /></div>
          </>
        ) : (
          <>
            {summary?.avgLtv != null ? (
              <div className="flex flex-col gap-4">
                {/* Average Portfolio LTV is a percentage against a 100 limit, which
                    is a named MetricGauge case in the kit. It carries NO
                    thresholds: no contract field on this read supplies a
                    warning/critical LTV band, and inventing one would present a
                    manufactured band as the FI's own. Without thresholds the kit
                    draws the value and bar in neutral colors and prints no
                    status verdict (MetricGauge getStatusText returns null). */}
                <MetricGauge
                  title="Average Portfolio LTV"
                  value={summary.avgLtv}
                  max={100}
                  unit="%"
                />
                {(summary.ltvTrend?.length ?? 0) >= 2 && (
                  <AreaChart
                    title="Average Portfolio LTV Trend"
                    data={toTrendSeries(summary.ltvTrend ?? [])}
                    dataKey="value"
                    xAxisKey="point"
                    color="rgb(var(--color-accent))"
                    formatValue={formatPercent}
                    height={170}
                  />
                )}
              </div>
            ) : (
              // MetricGauge's own shell and title, so the stand-in reads as the
              // gauge it replaces. Every field of the summary is optional, so a
              // successful read that omits avgLtv is 'Not reported', the tiles'
              // wording for the same absence. 'No LTV data available' stated a
              // measured empty the read never made.
              <GaugeUnavailable title="Average Portfolio LTV">
                {withheldCaption ?? NOT_REPORTED_CAPTION}
              </GaugeUnavailable>
            )}
            {summary?.concentrationRisk != null ? (
              <div className="flex flex-col gap-4">
                {/* summary.concentrationRisk is the largest collateral package's
                    share of the institution's total market value
                    (collateral-monitoring.service.ts), so the gauge is titled for
                    what it measures. A book of one package reads 100%. The
                    Concentration Risk page measures something else, open
                    breaches per risk limit, and is linked beside it rather than
                    sharing its name.

                    It carries NO thresholds, like Average Portfolio LTV beside
                    it: no contract field and no FI policy supplies a band for
                    this figure, and the 30/40 band it used to take printed a red
                    CRITICAL verdict no arbiter produced. */}
                <MetricGauge
                  title="Largest Package Share"
                  value={summary.concentrationRisk}
                  max={100}
                  unit="%"
                />
                <p className="text-xs font-sans text-tertiary">
                  Share of total collateral value in the largest collateral package.{' '}
                  <Link to="/monitoring/concentration" className="text-accent hover:underline">
                    View Concentration Risk
                  </Link>
                </p>
                {(summary.concentrationTrend?.length ?? 0) >= 2 && (
                  <AreaChart
                    title="Largest Package Share Trend"
                    data={toTrendSeries(summary.concentrationTrend ?? [])}
                    dataKey="value"
                    xAxisKey="point"
                    color="rgb(var(--color-neutral-data))"
                    formatValue={formatPercent}
                    height={170}
                  />
                )}
              </div>
            ) : (
              // A successful read that omits the figure reports it absent. The
              // institution's concentration limits and their open breaches are
              // on the Concentration Risk page; the stand-in points there instead
              // of stating that no data exists.
              <GaugeUnavailable title="Largest Package Share">
                <p>{withheldCaption ?? NOT_REPORTED_CAPTION}</p>
                <p className="mt-2">
                  Concentration limits and their breaches are tracked per risk limit.{' '}
                  <Link to="/monitoring/concentration" className="text-accent hover:underline">
                    View Concentration Risk
                  </Link>
                </p>
              </GaugeUnavailable>
            )}
            <div className="lg:col-span-2">
              {distributionOutcome === 'loading' ? (
                <Skeleton className="h-[250px]" />
              ) : distributionSegments.length > 0 ? (
                <div>
                  <DonutChart
                    title="Collateral Distribution by Asset Class"
                    data={distributionSegments}
                    size={200}
                    thickness={36}
                    centerLabel="Total"
                    centerValue={formatUsdCompact(distributionValuedTotal)}
                    formatTotal={formatUsdCompact}
                  />
                  {/* The center total is a sum over what the chart drew, so the
                      rows it could not draw are named right beside it. A caption
                      here is the page's own element: DonutChart exposes no
                      caption slot, the way MetricCard now does. */}
                  {distributionUnvalued && (
                    <p className="mt-2 text-xs font-sans text-tertiary">
                      {`Withheld from the chart and the total, no value in the response: ${distributionUnvalued}.`}
                    </p>
                  )}
                </div>
              ) : (
                <Card>
                  <div className="py-12 text-center text-tertiary">
                    {distributionUnvalued
                      ? `Collateral distribution data was returned without values: ${distributionUnvalued}.`
                      : sectionMessage(
                          distributionOutcome,
                          'Collateral distribution data',
                          'No collateral distribution data available',
                          distribution,
                        )}
                  </div>
                </Card>
              )}
            </div>
          </>
        )}
      </div>

      {/* Collateral Value Trend */}
      <div className="mb-8">
        {trendOutcome === 'loading' ? (
          <Skeleton className="h-[250px]" />
        ) : trendPoints && trendPoints.length > 0 ? (
          <AreaChart
            title={`Collateral Value (Last ${VALUE_TREND_HOURS} Hours)`}
            data={trendPoints}
            dataKey="value"
            xAxisKey="time"
            color="rgb(var(--color-growth))"
            formatValue={formatUsdCompact}
            height={250}
          />
        ) : (
          <Card>
            <div className="py-12 px-4 text-center text-tertiary">
              {/* The measured-empty sentence says why nothing is drawn: the
                  read covers only the last few hours of valuations. */}
              <p>
                {sectionMessage(
                  trendOutcome,
                  'Collateral value trend data',
                  VALUE_TREND_EMPTY_MESSAGE,
                  trendPoints,
                )}
              </p>
              {trendOutcome === 'read' && trendPoints !== undefined && (
                <p className="mt-2 text-sm">
                  <Link to="/monitoring/valuation-history" className="text-accent hover:underline">
                    Valuation History lists every valuation
                  </Link>
                </p>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Active Collateral Deficiency Notices. The dashboards' one section
          label (dashboard-chrome.tsx): the 40px serif heading here outranked
          the page title. DataTable draws its own card, so it is not wrapped in
          a second. */}
      <DashboardSectionLabel className="mt-8">Active Collateral Deficiency Notices</DashboardSectionLabel>
      {deficiencyOutcome === 'loading' ? (
        <SkeletonTable rows={3} columns={7} />
      ) : deficiencies && deficiencies.length > 0 ? (
        <DataTable
          columns={deficiencyColumns}
          data={deficiencies}
          striped
          hoverable
        />
      ) : (
        <Card>
          {/* The failed branch was a hand-rolled red callout saying only
              'Failed to load deficiency data.' It is folded into the same
              section message the other two sections use, so one state machine
              covers unread, failed and measured-empty rather than two. */}
          <div className="py-12 text-center text-tertiary">
            {sectionMessage(
              deficiencyOutcome,
              'Collateral deficiency notices',
              'No active collateral deficiency notices',
              deficiencies,
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
